import { DomainError, TransportError } from "@econ-v1/domain";
import { ClientRpcFrameReader, ClientRpcStreamClosedError, encodeClientRpcFrame, CLIENT_RPC_VERSION, } from "../client-rpc/frame.js";
import { clientRpcResponseError, L402PaymentChallengeError } from "../client-rpc/response-error.js";
import { defaultWebTransportFactory, } from "./contract.js";
/** Matches `CLIENT_RPC_WEBTRANSPORT_PATH` in `core/adapters-network::webtransport::client_rpc`. */
const CLIENT_RPC_WEBTRANSPORT_PATH = "/node/client-rpc/1";
/** Matches `CLIENT_TRANSPORT_IDENTITY_HEADER` on the Rust adapter — see that module's docs for why WebTransport has to self-declare this instead of relying on a transport-observed peer identity the way iroh does. */
const CLIENT_TRANSPORT_IDENTITY_HEADER = "x-client-transport-identity";
/**
 * Browser client for the hash-pinned LAN WebTransport carrier (ticket
 * #1823). Implements the existing `HomeNodeClient` port beside
 * `HttpWsHomeNodeClient` and `IrohHomeNodeClient`.
 *
 * One WebTransport session is opened lazily and reused across calls (mirrors
 * `IrohHomeNodeClient`'s persistent endpoint); each `invoke`/`deltas` call
 * opens a fresh bidirectional stream and runs one setup + request + response
 * exchange on it, matching the Rust adapter's per-stream session-setup loop
 * (`core/adapters-network::webtransport::client_rpc`). Closing the write side
 * after the request frame lets the server's read loop observe a clean EOF
 * (`ClientRpcFrameError::Io(UnexpectedEof)` on that side, handled as a normal
 * stream close, not an error) instead of blocking on another frame that will
 * never arrive.
 *
 * Backpressure: every write goes through `WritableStreamDefaultWriter.write`,
 * whose returned promise does not resolve until the stream's queuing
 * strategy accepts the chunk — the same backpressure signal the Streams API
 * gives any other writer, not a custom scheme.
 *
 * Cancellation: the existing `HomeNodeClient` port does not carry a
 * per-call `AbortSignal` (neither does `IrohHomeNodeClient`), so there is no
 * meaning of live conversational cancellation to add without changing that
 * port. What this client does cancel is connection lifecycle: `close()`
 * aborts every still-open stream and the session itself immediately, so a
 * transport-manager-driven fallback never leaves an orphaned WebTransport
 * session or in-flight request behind.
 */
export class WebTransportHomeNodeClient {
    #candidate;
    #transportIdentity;
    #setupHeaders;
    #capabilityForOperation;
    #webTransportFactory;
    #openStreams = new Set();
    #session;
    #sessionReady;
    #closed = false;
    state;
    constructor(options) {
        this.#candidate = options.candidate;
        this.#transportIdentity = options.transportIdentity;
        this.#setupHeaders = options.setupHeaders ?? (async () => ({}));
        this.#capabilityForOperation = options.capabilityForOperation ?? ((operation) => operation);
        this.#webTransportFactory = options.webTransportFactory ?? defaultWebTransportFactory;
        const now = options.now ?? (() => new Date().toISOString());
        this.state = { changedAt: now(), path: "lan-webtransport", status: "connected" };
    }
    async invoke(request) {
        return await this.#request({
            body: request.payload,
            capability: this.#capabilityForOperation(request.operation),
            ...(request.headers === undefined ? {} : { headers: request.headers }),
            route: request.operation,
        });
    }
    async *deltas(request) {
        const response = await this.#request({
            body: { cursor: request.cursor, domain: request.domain },
            capability: "core.events.read",
            route: "client-sync.deltas",
        });
        if (!Array.isArray(response)) {
            throw new TransportError({ code: "unavailable", message: "WebTransport delta response was malformed" });
        }
        for (const delta of response)
            yield delta;
    }
    async close() {
        if (this.#closed)
            return;
        this.#closed = true;
        const streams = [...this.#openStreams];
        this.#openStreams.clear();
        await Promise.all(streams.map(async (stream) => await abortStream(stream)));
        const session = this.#session;
        this.#session = undefined;
        this.#sessionReady = undefined;
        try {
            session?.close({ closeCode: 0, reason: "client closed" });
        }
        catch {
            // Best-effort: the session may already be gone.
        }
    }
    async #getSession() {
        if (this.#closed) {
            throw new TransportError({ code: "offline", message: "WebTransport home-node client is closed" });
        }
        this.#sessionReady ??= this.#openSession();
        return await this.#sessionReady;
    }
    async #openSession() {
        const url = new URL(CLIENT_RPC_WEBTRANSPORT_PATH, this.#candidate.address).toString();
        let session;
        try {
            session = this.#webTransportFactory(url, {
                serverCertificateHashes: [
                    { algorithm: "sha-256", value: hexToBytes(this.#candidate.certificateHashes.current) },
                    { algorithm: "sha-256", value: hexToBytes(this.#candidate.certificateHashes.next) },
                ],
            });
            await session.ready;
        }
        catch {
            this.#sessionReady = undefined;
            throw new TransportError({ code: "offline", message: "WebTransport session failed to establish" });
        }
        this.#session = session;
        return session;
    }
    async #request(request) {
        const session = await this.#getSession();
        let stream;
        try {
            stream = await session.createBidirectionalStream();
        }
        catch {
            throw new TransportError({ code: "offline", message: "WebTransport stream failed to open" });
        }
        this.#openStreams.add(stream);
        const writer = stream.writable.getWriter();
        const reader = stream.readable.getReader();
        const frameReader = new ClientRpcFrameReader(reader);
        try {
            const setupHeaders = await this.#setupHeaders();
            await writer.write(encodeClientRpcFrame({
                type: "setup",
                version: CLIENT_RPC_VERSION,
                headers: { ...setupHeaders, [CLIENT_TRANSPORT_IDENTITY_HEADER]: this.#transportIdentity },
            }));
            await writer.write(encodeClientRpcFrame({
                type: "request",
                version: CLIENT_RPC_VERSION,
                id: requestId(),
                method: "POST",
                route: request.route,
                capability: request.capability,
                headers: request.headers ?? {},
                body: request.body,
            }));
            // Signal "no more frames on this stream": lets the server's read loop
            // observe a clean EOF once it finishes this request instead of idling
            // on a stream we will never write to again.
            await writer.close();
            const frame = await frameReader.readFrame();
            return responseBody(frame);
        }
        catch (error) {
            // A `DomainError` or `L402PaymentChallengeError` from `responseBody`
            // below (e.g. a scope denial or an L402 payment challenge) must pass
            // through unchanged — swallowing either into the generic
            // `TransportError` below would resurrect ticket #1826's bug where a
            // domain/authorization failure (or, per #1866, a payment challenge)
            // incorrectly triggers a network failover.
            if (error instanceof TransportError || error instanceof DomainError || error instanceof L402PaymentChallengeError)
                throw error;
            if (error instanceof ClientRpcStreamClosedError) {
                throw new TransportError({ code: "connection_lost", message: "WebTransport stream closed before a response arrived" });
            }
            throw new TransportError({ code: "offline", message: "WebTransport home-node request failed" });
        }
        finally {
            frameReader.releaseLock();
            try {
                writer.releaseLock();
            }
            catch {
                // Already released by `writer.close()` in the success path.
            }
            await reader.cancel().catch(() => undefined);
            this.#openStreams.delete(stream);
        }
    }
}
function responseBody(frame) {
    if (frame.type !== "response") {
        throw new TransportError({ code: "unavailable", message: "WebTransport client-rpc response was malformed" });
    }
    if (frame.status >= 400 || frame.error !== undefined) {
        throw clientRpcResponseError(frame.status, frame.error, "WebTransport client-rpc request failed", frame.body);
    }
    return frame.body;
}
async function abortStream(stream) {
    await Promise.all([
        stream.writable.abort().catch(() => undefined),
        stream.readable.cancel().catch(() => undefined),
    ]);
}
function requestId() {
    return `client-rpc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function hexToBytes(hex) {
    if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) {
        throw new TransportError({ code: "unavailable", message: "WebTransport certificate hash is not valid hex" });
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
    }
    return bytes;
}
//# sourceMappingURL=webtransport-home-node-client.js.map